import { useGetCanceledJobsQuery, useGetMovesQuery, useMoveActionsMutation } from "@/store/moves/apis";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import MoveInvoice from "@/pages/Moves/MoveInvoice";
import { Button } from "../../components/ui/button";
import { formatDateWithOrdinal, formatStartWindow } from "@/lib/utils";
import { parseISO } from "date-fns";
import { Move } from "@/types";
import { startOfDay, isSameDay, addDays, isAfter } from "date-fns";
import { useEffect, useState } from "react";
import { CREW_ROLES, MOVE_KINDS, SERVICE_TYPES, CANCEL_REASONS } from "@/constants";
import { useSearchParams, useNavigate, useParams } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { AssignCrew } from "./AssignCrew";
import { Loading } from "@/components/Loading";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faPeopleCarry,
} from "@fortawesome/free-solid-svg-icons";

const INCLUDE_ASSOCIATIONS = {
  upcoming: ["customer", "sales", "locations", "billing", "crew"],
  past: ["customer", "billing"],
};

const TABS = {
  upcoming: {
    label: "Upcoming",
    scopes: "upcoming",
    sort: "asc",
    path: "/moves/upcoming",
  },
  past_unpaid: {
    label: "Past (unpaid)",
    scopes: "past,unpaid",
    sort: "desc",
    path: "/moves/past?filter=unpaid",
  },
  past_paid: {
    label: "Past (paid)",
    scopes: "past,paid",
    sort: "desc",
    path: "/moves/past?filter=paid",
  },
  past_all: {
    label: "Past (all)",
    scopes: "past",
    sort: "desc",
    path: "/moves/past",
  },
  canceled: {
    label: "Canceled",
    scopes: "canceled",
    sort: "desc",
    path: "/moves/canceled",
  },
} as const;

type TabKey = keyof typeof TABS;

const Jobs = () => {
  const { view = "upcoming" } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const filter = searchParams.get("filter");
  const [moveActions] = useMoveActionsMutation();
  const { toast } = useToast();
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [selectedMoveForCrew, setSelectedMoveForCrew] = useState<
    Move | undefined
  >(undefined);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedMove, setSelectedMove] = useState<Move | undefined>(undefined);
  const [partitionedJobs, setPartitionedJobs] = useState<
    { label: string | null; jobs: Move[] }[]
  >([]);
  const getActiveTabFromUrl = () => {
    if (view === "upcoming") return "upcoming";
    if (view === "canceled") return "canceled";
    if (view === "past") {
      if (!filter) return "past_all";
      if (filter === "unpaid") return "past_unpaid";
      if (filter === "paid") return "past_paid";
    }

    return "upcoming";
  };
  const [activeTab, setActiveTab] = useState<TabKey>(getActiveTabFromUrl());
  const {
    data: jobs,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetMovesQuery(
    {
      type: "jobs",
      filters: TABS[activeTab].scopes,
      sort: `service_date ${TABS[activeTab].sort}`,
      include:
        INCLUDE_ASSOCIATIONS[
          activeTab.startsWith("past") ? "past" : "upcoming"
        ],
      limit: 50,
    },
    { skip: !activeTab }
  );

  const {
    data: canceledJobs,
    isLoading: isLoadingCanceled,
    isFetching: isFetchingCanceled,
    error: errorCanceled,
    refetch: refetchCanceled,
  } = useGetCanceledJobsQuery(
    {
      sort: `service_date ${TABS[activeTab].sort}`,
      include: INCLUDE_ASSOCIATIONS["past"],
      limit: 50,
    },
    { skip: activeTab !== "canceled" }
  );

  useEffect(() => {
    const newActiveTab = getActiveTabFromUrl();
    if (newActiveTab !== activeTab) {
      setActiveTab(newActiveTab);
      setPartitionedJobs([]);
      if (newActiveTab === "canceled") {
        refetchCanceled();
      } else {
        refetch();
      }
    }
  }, [activeTab, filter]);

  const handleTabChange = (key: TabKey) => {
    console.log("handleTabChange", key, activeTab);
    if (activeTab !== key) {
      setActiveTab(key);
      setPartitionedJobs([]);
      const { path } = TABS[key];
      navigate(path);
      if (key === "canceled") {
        refetchCanceled();
      } else {
        refetch();
      }
    }
  };

  useEffect(() => {
    const partitionJobs = (jobs: Move[]) => {
      if (activeTab === "canceled") {
        setPartitionedJobs([{ label: null, jobs }]);
        return;
      }
      
      if (activeTab.startsWith("past")) {
        setPartitionedJobs([{ label: null, jobs }]);
        return;
      }
      const today = startOfDay(new Date());
      const tomorrow = addDays(today, 1);

      setPartitionedJobs([
        {
          label: "Today",
          jobs: jobs.filter((job) =>
            isSameDay(parseISO(job.service_date.toString()), today)
          ),
        },
        {
          label: "Tomorrow",
          jobs: jobs.filter((job) =>
            isSameDay(parseISO(job.service_date.toString()), tomorrow)
          ),
        },
        {
          label: "Future",
          jobs: jobs.filter((job) =>
            isAfter(parseISO(job.service_date.toString()), tomorrow)
          ),
        },
      ]);
    };

    if (activeTab === "canceled" && canceledJobs) {
      partitionJobs(canceledJobs);
    } else if (jobs) {
      partitionJobs(jobs);
    }
  }, [activeTab, jobs, canceledJobs]);

  const actions = (job: Move) => {
    // if (job.status === "booked" || job.status === "in_progress") {
    //   return (
    //     <>
    //       <Button
    //         variant="link"
    //         className="text-green-700 pl-0 underline "
    //         onClick={() => {
    //           markAsCompleted(job);
    //         }}
    //       >
    //         Mark as Completed
    //       </Button>
    //     </>
    //   );
    // }

    return (
      <>
        <Button
          variant="link"
          className="text-blue-800 pl-0 underline py-0 h-6"
          onClick={() => {
            setSelectedMove(job);
            setIsDialogOpen(true);
          }}
        >
          {job.status === "completed" ? "Update Invoice" : "Finalize Job"}
        </Button>
        {job.invoice?.total &&
          job.invoice?.due_amount &&
          job.invoice?.due_amount > 0 && (
            <Button
              variant="link"
              className="text-blue-800 pl-0 underline py-0 h-6"
              onClick={() => {
                sendInvoice(job);
              }}
            >
              Send Invoice
            </Button>
          )}
      </>
    );
  };

  // const markAsCompleted = async (job: Move) => {
  //   try {
  //     await moveActions({
  //       id: job.id,
  //       action: "set_to_completed",
  //     }).unwrap();

  //     refetch();
  //   } catch (err) {
  //     console.log("err", err);
  //   }
  // };

  const sendInvoice = async (job: Move) => {
    try {
      await moveActions({
        id: job.id,
        action: "send_invoice",
      }).unwrap();

      toast({
        title: "Success",
        description: `Invoice sent to ${job.customer_name}`,
      });
    } catch (err) {
      console.log("err", err);
    }
  };

  // const sendReceipt = async (job: Move) => {
  //   try {
  //     await moveActions({
  //       id: job.id,
  //       action: "send_receipt",
  //     }).unwrap();

  //     toast({
  //       title: "Success",
  //       description: `Receipt sent to ${job.customer_name}`,
  //     });
  //   } catch (err) {
  //     console.log("err", err);
  //   }
  // };

  const upcomingSummary = () => {
    return (
      <div className="flex items-center gap-2">
        <div className="text-base">
          <span className="text-lg font-semibold">{jobs?.length}</span>{" "}
          scheduled
        </div>
      </div>
    );
  };

  const pastSummary = () => {
    return null;
  };

  if (error || (activeTab === "canceled" && errorCanceled)) return <div>Error loading jobs</div>;

  return (
    <div className="p-4">
      <div className="flex lg:flex-row flex-col justify-between items-center">
        <h1 className="text-2xl font-bold mb-1">Jobs</h1>
        <div>
          {activeTab === "upcoming" ? upcomingSummary() : pastSummary()}
        </div>
      </div>

      <div className="mt-4 flex items-baseline border-b border-stone-500">
        {Object.entries(TABS).map(([key, tab]) => (
          <div
            key={key}
            className={`px-4 py-1 text-sm cursor-pointer border border-stone-300 ${
              activeTab === key ? "text-black font-semibold bg-stone-100" : ""
            }`}
            onClick={() => handleTabChange(key as TabKey)}
          >
            {tab.label}
          </div>
        ))}
      </div>

      <Table className="mt-4">
        <TableHeader>
          {activeTab === "upcoming" ? (
            <TableRow>
              <TableHead className="w-[10%]">Date</TableHead>
              <TableHead className="w-[10%]">Time</TableHead>
              <TableHead className="w-[25%]">Customer</TableHead>
              <TableHead className="w-[20%]">Service</TableHead>
              <TableHead className="w-[15%]">Resources</TableHead>
              <TableHead className="w-[10%]">Total (est)</TableHead>
              <TableHead className="w-[10%]">Action</TableHead>
            </TableRow>
          ) : activeTab === "canceled" ? (
            <TableRow>
              <TableHead className="w-[15%]">Date</TableHead>
              <TableHead className="w-[35%]">Customer</TableHead>
              <TableHead className="w-[50%]">Cancel Reason</TableHead>
            </TableRow>
          ) : (
            <TableRow>
              <TableHead className="w-[10%]">Date</TableHead>
              <TableHead className="w-[25%]">Customer</TableHead>
              <TableHead className="w-[10%]">Total</TableHead>
              <TableHead className="w-[15%]">Status</TableHead>
              <TableHead className="w-[25%]">Actions</TableHead>
            </TableRow>
          )}
        </TableHeader>
        {(isLoading || isFetching) || (activeTab === "canceled" && (isLoadingCanceled || isFetchingCanceled)) ? (
          <Loading />
        ) : (
          <TableBody>
            {partitionedJobs.map(({ label, jobs }) => (
              <>
                {label && (
                  <TableRow key={label} className="bg-muted/50">
                    <TableCell
                      colSpan={activeTab === "upcoming" ? 7 : activeTab === "canceled" ? 3 : 5}
                      className="text-base font-semibold"
                    >
                      {label}
                    </TableCell>
                  </TableRow>
                )}
                {jobs.length > 0 ? (
                  jobs.map((job: Move) => (
                    <TableRow key={job.id}>
                      <TableCell
                        className="cursor-pointer"
                        onClick={(e) => {
                          e.preventDefault();
                          window.open(`/move/${job.id}`, "_blank");
                        }}
                      >
                        {formatDateWithOrdinal(
                          parseISO(job.service_date.toString()),
                          { showYear: false }
                        )}
                      </TableCell>
                      {activeTab === "upcoming" && (
                        <TableCell
                          className="cursor-pointer"
                          onClick={(e) => {
                            e.preventDefault();
                            window.open(`/move/${job.id}`, "_blank");
                          }}
                        >
                          <span>
                            {formatStartWindow(
                              job.start_window_begin_hour,
                              job.start_window_begin_minute,
                              job.start_window_begin_meridiem
                            )}
                          </span>
                          <br />
                          <span className="text-sm text-gray-800">
                            {Number(job.hours_estimate).toFixed(0)} hours
                          </span>
                        </TableCell>
                      )}
                      <TableCell
                        className="font-semibold cursor-pointer"
                        onClick={(e) => {
                          e.preventDefault();
                          window.open(`/move/${job.id}`, "_blank");
                        }}
                      >
                        {job.customer_name}
                      </TableCell>
                      {activeTab === "canceled" && (
                        <TableCell
                          className="cursor-pointer"
                          onClick={(e) => {
                            e.preventDefault();
                            window.open(`/move/${job.id}`, "_blank");
                          }}
                        >
                          <div>
                            {job.status_info && (
                              <div className="text-sm text-gray-800 font-semibold mt-1">
                                {CANCEL_REASONS[job.status_info as keyof typeof CANCEL_REASONS] || job.status_info}
                              </div>
                            )}
                          </div>
                        </TableCell>
                      )}
                      {activeTab === "upcoming" && (
                        <TableCell
                          className="cursor-pointer"
                          onClick={(e) => {
                            e.preventDefault();
                            window.open(`/move/${job.id}`, "_blank");
                          }}
                        >
                          <div className="font-medium">
                            {SERVICE_TYPES[
                              job.service as keyof typeof SERVICE_TYPES
                            ] ||
                              job.service ||
                              "N/A"}
                          </div>
                          <div>
                            {job.origin.city}
                            {job.stops.length > 0 &&
                              job.stops.map((stop) => stop.city).join(" -> ")}
                            {job.destination?.city
                              ? ` to ${job.destination?.city}`
                              : ""}
                          </div>
                          <br />
                          <span className="text-sm text-gray-800">
                            {job.kind === "apartment" || job.kind === "sfh"
                              ? `${job.size} ${
                                  MOVE_KINDS[
                                    job.kind as keyof typeof MOVE_KINDS
                                  ]
                                }`
                              : MOVE_KINDS[job.kind as keyof typeof MOVE_KINDS]}
                          </span>
                        </TableCell>
                      )}
                      {activeTab === "upcoming" && (
                        <TableCell
                          className="cursor-pointer"
                          onClick={(e) => {
                            e.preventDefault();
                            window.open(`/move/${job.id}`, "_blank");
                          }}
                        >
                          {job.num_of_movers} movers, {job.num_of_trucks} truck
                          {job.num_of_trucks && job.num_of_trucks > 1
                            ? "s"
                            : ""}
                          {job.assigned_crew?.length > 0 && (
                            <div className="text-sm text-gray-800 flex items-baseline gap-1">
                              <FontAwesomeIcon
                                icon={faPeopleCarry}
                                className="w-3 h-3"
                              />
                              {job.moving_crews
                                .map(
                                  (mc) =>
                                    `${mc.first_name} (${
                                      CREW_ROLES[mc.role][0]
                                    })`
                                )
                                .join(", ")}
                            </div>
                          )}
                        </TableCell>
                      )}
                      {activeTab === "upcoming" && (
                        <TableCell
                          className="cursor-pointer space-x-1"
                          onClick={(e) => {
                            e.preventDefault();
                            window.open(`/move/${job.id}`, "_blank");
                          }}
                        >
                          <span>
                            {job.invoice?.total && job.invoice?.total > 0 && (
                              <FontAwesomeIcon
                                icon={faCheckCircle}
                                className="w-3 h-3 text-green-700"
                              />
                            )}
                          </span>
                          <span>
                            {Intl.NumberFormat("en-US", {
                              style: "currency",
                              currency: "USD",
                            }).format(job.invoice?.total || job.revenue || 0)}
                          </span>
                        </TableCell>
                      )}
                      {activeTab !== "upcoming" && activeTab !== "canceled" && (
                        <TableCell
                          className="cursor-pointer"
                          onClick={(e) => {
                            e.preventDefault();
                            window.open(`/move/${job.id}`, "_blank");
                          }}
                        >
                          {job.invoice?.total
                            ? new Intl.NumberFormat("en-US", {
                                style: "currency",
                                currency: "USD",
                              }).format(job.invoice.total)
                            : "-"}
                        </TableCell>
                      )}
                      {activeTab !== "upcoming" && activeTab !== "canceled" && (
                        <TableCell
                          className="cursor-pointer"
                          onClick={(e) => {
                            e.preventDefault();
                            window.open(`/move/${job.id}`, "_blank");
                          }}
                        >
                          {job.invoice ? (
                            job.invoice.paid_in_full ? (
                              <>
                                <span className="text-green-700 font-semibold">
                                  Paid{" "}
                                </span>
                                <span className="text-sm text-gray-800">
                                  (receipt sent)
                                </span>
                              </>
                            ) : (
                              <span className="text-orange-700 font-semibold">
                                Payment Due
                              </span>
                            )
                          ) : (
                            <span className="text-black">
                              Missing Final Amounts
                            </span>
                          )}
                        </TableCell>
                      )}
                      {activeTab === "upcoming" && label === "Today" && (
                        <TableCell>{actions(job)}</TableCell>
                      )}
                      {activeTab === "upcoming" && (
                        <TableCell>
                          <Button
                            variant="link"
                            className="text-blue-800 pl-0 underline py-0 h-6"
                            onClick={() => {
                              setSelectedMoveForCrew(job);
                              setIsSheetOpen(true);
                            }}
                          >
                            Assign Crew
                          </Button>
                        </TableCell>
                      )}
                      {activeTab !== "upcoming" && activeTab !== "canceled" && (
                        <TableCell>{actions(job)}</TableCell>
                      )}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={activeTab === "upcoming" ? 7 : activeTab === "canceled" ? 3 : 5}
                      className="text-left"
                    >
                      No jobs
                    </TableCell>
                  </TableRow>
                )}
              </>
            ))}
          </TableBody>
        )}
      </Table>

      <Dialog
        open={isDialogOpen}
        onOpenChange={(open) => {
          setIsDialogOpen(open);
          if (!open) {
            setSelectedMove(undefined);
          }
        }}
      >
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="text-sm font-normal">#{selectedMove?.number}</div>
            <DialogTitle>{selectedMove?.customer_name}</DialogTitle>
            <DialogDescription>
              Enter the final amounts for the job.
            </DialogDescription>
          </DialogHeader>
          {selectedMove && (
            <MoveInvoice
              move={selectedMove}
              onSuccess={() => {
                setIsDialogOpen(false);
                setSelectedMove(undefined);
                refetch();
              }}
              onCancel={() => {
                setIsDialogOpen(false);
                setSelectedMove(undefined);
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      <AssignCrew
        move_id={selectedMoveForCrew?.id}
        open={isSheetOpen}
        onOpenChange={(open) => {
          setIsSheetOpen(open);
          if (!open) {
            setSelectedMoveForCrew(undefined);
            refetch();
          }
        }}
      />
    </div>
  );
};

export default Jobs;
